import type { Message } from '../../contexts/ConversationContext';

interface MessageBubbleProps {
  message: Message;
}

export default function MessageBubble({ message }: MessageBubbleProps) {
  const isUser = message.role === 'user';
  const isStreaming = message.isStreaming;

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[90%] md:max-w-[85%] px-3 md:px-4 py-2.5 md:py-3 ${
          isUser
            ? 'bg-black text-white'
            : 'bg-white text-black shadow-subtle border border-grey-medium/10'
        }`}
      >
        {/* Message content */}
        <div className="text-sm md:text-base font-light whitespace-pre-wrap break-words leading-relaxed">
          {message.content || (isStreaming && <LoadingPulse />)}
        </div>

        {/* Footer: Timestamp and indicators */}
        <div className="flex items-center justify-between gap-3 mt-2 pt-2 border-t border-current/10">
          {/* Timestamp */}
          <span
            className={`font-mono text-xs uppercase tracking-wider ${
              isUser ? 'text-white/50' : 'text-grey-medium'
            }`}
          >
            {formatTime(message.timestamp)}
          </span>

          {/* Visualization indicator */}
          {(message.html || message.a2ui) && (
            <div className="flex items-center gap-1.5">
              <svg
                className="w-3.5 h-3.5 text-mint"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.5}
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
              <span className="font-mono text-xs text-mint uppercase tracking-wider">Chart</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function LoadingPulse() {
  return (
    <div className="flex items-center gap-2">
      <div className="flex gap-1">
        <div className="w-2 h-2 bg-grey-medium rounded-full animate-pulse-subtle" style={{ animationDelay: '0ms' }} />
        <div className="w-2 h-2 bg-grey-medium rounded-full animate-pulse-subtle" style={{ animationDelay: '150ms' }} />
        <div className="w-2 h-2 bg-grey-medium rounded-full animate-pulse-subtle" style={{ animationDelay: '300ms' }} />
      </div>
      <span className="font-mono text-xs text-grey-medium uppercase tracking-wider">Thinking</span>
    </div>
  );
}

function formatTime(timestamp: number): string {
  const date = new Date(timestamp);
  return date.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  });
}
